import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  Divider,
  Alert,
  Grid,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useBooking } from '../../../contexts/BookingContext';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PhoneInput from '../../../components/common/PhoneInput';
import OtherChargesSection from '../OtherCharges';

const PAYMENT_PLANS = [
  {
    value: "10_10_80",
    label: "10-10-80",
    description: "10% booking, 10% agreement, 80% possession"
  },
  {
    value: "10_20_70",
    label: "10-20-70",
    description: "10% booking, 20% construction, 70% possession"
  },
  {
    value: "10_30_60",
    label: "10-30-60",
    description: "10% booking, 30% construction, 60% possession"
  }
];

export default function EditBookingDetails({ open, onClose, booking, otherChargesSelect }) {
  const { updateBooking } = useBooking();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    additional_customers: [],
    newCustomerName: '',
    newCustomerEmail: '',
    newCustomerPhone: '',
    project: '',
    unit: '',
    agent: '',
    lead: '',
    base_price: '',
    token_amount: '',
    token_amount_pct: '',
    booking_amount: '',
    booking_amount_pct: '',
    other_charges: [],
    newChargeType: '',
    newChargeAmount: '',
    newChargeDescription: '',
    tax: '',
    taxes: '',
    total_price: '',
    payment_plan: {},
    notes: '',
    terms_accepted: false,
    expiry_date: null,
    completion_date: null,
  });

  // Add these new states for charge editing
  const [chargeDialogOpen, setChargeDialogOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [chargeFormData, setChargeFormData] = useState({
    type: '',
    amount: '',
    description: ''
  });

  // Initialize form data when booking changes
  useEffect(() => {
    if (booking) {
      const base_price = parseFloat(booking.sale_order?.base_price) || 0;
      const booking_amount = parseFloat(booking.sale_order?.booking_amount) || 0;
      const token_amount = parseFloat(booking.sale_order?.token_amount) || 0;

      // Calculate percentages
      const booking_amount_pct = base_price ? ((booking_amount / base_price) * 100).toFixed(2) : '';
      const token_amount_pct = booking_amount ? ((token_amount / booking_amount) * 100).toFixed(2) : '';

      setFormData({
        // Customer Information
        customer_name: booking.customer?.name || '',
        customer_email: booking.customer?.email || '',
        customer_phone: booking.customer?.phone || '',
        additional_customers: booking.additional_customers || [],
        newCustomerName: '',
        newCustomerEmail: '',
        newCustomerPhone: '',

        // Project/Unit/Agent/Lead (non-editable, for display)
        project: booking.project?.name || '',
        unit: booking.unit?.name || '',
        agent: booking.agent?.name || '',
        lead: booking.lead?.name || '',

        // Finance Section
        base_price: base_price.toString(),
        token_amount: token_amount.toString(),
        token_amount_pct: token_amount_pct,
        booking_amount: booking_amount.toString(),
        booking_amount_pct: booking_amount_pct,
        other_charges: booking.sale_order?.other_charges || [],
        newChargeType: '',
        newChargeAmount: '',
        newChargeDescription: '',
        tax: booking.sale_order?.taxes?.toString() || '',
        taxes: booking.sale_order?.taxes?.toString() || '',
        total_price: booking.sale_order?.total_price?.toString() || '',

        // Other fields
        payment_plan: booking.payment_plan || {},
        notes: booking.notes || '',
        terms_accepted: booking.terms_accepted || false,
        expiry_date: booking.expiry_date ? dayjs(booking.expiry_date) : null,
        completion_date: booking.completion_date ? dayjs(booking.completion_date) : null,
      });
    }
  }, [booking]);

  // Booking Amount sync
  useEffect(() => {
    syncBookingAmount(
      parseFloat(formData.base_price) || 0,
      formData.booking_amount,
      formData.booking_amount_pct,
      formData._lastEdited,
      setFormData
    );
    // eslint-disable-next-line
  }, [formData.booking_amount, formData.booking_amount_pct, formData.base_price]);

  // Token Amount sync
  useEffect(() => {
    syncTokenAmount(
      parseFloat(formData.booking_amount) || 0,
      formData.token_amount,
      formData.token_amount_pct,
      formData._lastEdited,
      setFormData
    );
    // eslint-disable-next-line
  }, [formData.token_amount, formData.token_amount_pct, formData.booking_amount]);

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.customer_name) {
      errors.customer_name = 'Customer name is required';
    }
    
    if (!formData.customer_email) {
      errors.customer_email = 'Customer email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.customer_email)) {
      errors.customer_email = 'Invalid email format';
    }
    
    if (!formData.customer_phone) {
      errors.customer_phone = 'Customer phone is required';
    } else if (!/^\d{10}$/.test(formData.customer_phone.replace(/[^0-9]/g, ''))) {
      errors.customer_phone = 'Invalid phone number format';
    }

    if (formData.booking_amount && isNaN(formData.booking_amount)) {
      errors.booking_amount = 'Booking amount must be a number';
    }

    if (formData.base_price && isNaN(formData.base_price)) {
      errors.base_price = 'Base price must be a number';
    }

    if (formData.taxes && isNaN(formData.taxes)) {
      errors.taxes = 'Taxes must be a number';
    }

    if (formData.total_price && isNaN(formData.total_price)) {
      errors.total_price = 'Total price must be a number';
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setError('Please fix the validation errors');
      return;
    }

    // Check if booking exists
    if (!booking || !booking.id) {
      setError('Invalid booking data');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        expiry_date: formData.expiry_date ? formData.expiry_date.toISOString() : null,
        completion_date: formData.completion_date ? formData.completion_date.toISOString() : null,
        booking_amount: Math.round(Number(formData.booking_amount)) || 0,
        base_price: Math.round(Number(formData.base_price)) || 0,
        taxes: Math.round(Number(formData.taxes)) || 0,
        total_price: Math.round(Number(formData.total_price)) || 0,
        other_charges: formData.other_charges.map(charge => ({
          ...charge,
          amount: Math.round(Number(charge.amount)) || 0
        }))
      };
      await updateBooking(booking.id, payload);
      onClose('updated');
    } catch (err) {
      setError(err.message || 'Failed to update booking');
    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = (field) => (event) => {
    let value = event.target.value;
    // Cap % fields at 100
    if ((field === "booking_amount_pct" || field === "token_amount_pct") && parseFloat(value) > 100) {
      value = "100";
    }
    setFormData(prev => ({
      ...prev,
      [field]: value,
      _lastEdited: field
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleDateChange = (field) => (value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle charge dialog
  const openChargeDialog = (charge = null) => {
    if (charge) {
      setEditingCharge(charge);
      setChargeFormData({
        type: charge.type,
        amount: charge.amount,
        description: charge.description
      });
    } else {
      setEditingCharge(null);
      setChargeFormData({
        type: '',
        amount: '',
        description: ''
      });
    }
    setChargeDialogOpen(true);
  };

  const closeChargeDialog = () => {
    setChargeDialogOpen(false);
    setEditingCharge(null);
    setChargeFormData({
      type: '',
      amount: '',
      description: ''
    });
  };

  
  // When booking amount or taxes change, recalculate total price
  useEffect(() => {
    const basePrice = parseInt(formData.base_price) || 0;
    const otherCharges = formData.other_charges.reduce(
      (sum, charge) => sum + (parseInt(charge.amount) || 0), 0
    );
    const bookingAmount = parseInt(formData.booking_amount) || 0;
    const tokenAmount = parseInt(formData.token_amount) || 0;
    const tax = parseInt(formData.tax) || 0;

    const subtotal = parseInt(basePrice + otherCharges);
    const taxAmount = parseInt(Math.floor(subtotal * tax / 100));
    const total = parseInt(subtotal + taxAmount);

    setFormData(f => ({
      ...f,
      taxes: taxAmount,
      subtotal: subtotal,
      total_price: total
    }));
    // eslint-disable-next-line
  }, [formData.base_price, formData.tax, formData.other_charges, formData.booking_amount, formData.token_amount]);


  const handleSaveCharge = () => {
    // Check if this type already exists in other charges (except for the current editing charge)
    const typeExists = formData.other_charges.some(charge => 
      charge.type === chargeFormData.type && charge !== editingCharge
    );

    if (typeExists) {
      setError('This charge type already exists');
      return;
    }

    const selectedType = otherChargesSelect.find(type => type.key === chargeFormData.type);
    
    const newCharge = {
      ...chargeFormData,
      label: selectedType?.label || '', // Get label from otherChargesSelect
      amount: parseFloat(chargeFormData.amount)
    };

    setFormData(prev => ({
      ...prev,
      other_charges: editingCharge
        ? prev.other_charges.map(charge => 
            charge === editingCharge ? newCharge : charge
          )
        : [...prev.other_charges, newCharge]
    }));

    closeChargeDialog();
  };

  const handleDeleteCharge = (chargeToDelete) => {
    setFormData(prev => ({
      ...prev,
      other_charges: prev.other_charges.filter(charge => charge !== chargeToDelete)
    }));
  };

  // Sync booking_amount and booking_amount_pct based on base_price
  const syncBookingAmount = (base, value, pct, lastEdited, setData) => {
    if (!base) return;
    if (lastEdited === "booking_amount") {
      let computedPct = Math.round((parseFloat(value) / base) * 100);
      if (computedPct > 100) computedPct = 100;
      setData(f => ({ ...f, booking_amount_pct: computedPct }));
    } else if (lastEdited === "booking_amount_pct") {
      let pctVal = Math.min(parseInt(pct), 100);
      let computedVal = ((pctVal / 100) * base);
      setData(f => ({ ...f, booking_amount: computedVal.toFixed(2), booking_amount_pct: pctVal }));
    }
  };

  // Sync token_amount and token_amount_pct based on booking_amount
  const syncTokenAmount = (booking, value, pct, lastEdited, setData) => {
    if (!booking) return;
    if (lastEdited === "token_amount") {
      let computedPct = Math.round((parseFloat(value) / booking) * 100);
      if (computedPct > 100) computedPct = 100;
      setData(f => ({ ...f, token_amount_pct: computedPct }));
    } else if (lastEdited === "token_amount_pct") {
      let pctVal = Math.min(parseInt(pct), 100);
      let computedVal = ((pctVal / 100) * booking);
      setData(f => ({ ...f, token_amount: computedVal.toFixed(2), token_amount_pct: pctVal }));
    }
  };

  if (!booking) return null;
  return (
    <Dialog 
      open={open} 
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box>
          <Typography variant="h6">
            Edit Booking Details
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Booking ID: {booking.booking_id}
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Non-editable Information */}
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Non-editable Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Lead:</strong> {booking.lead?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Agent:</strong> {booking.agent?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Project:</strong> {booking.project?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Unit:</strong> {booking.unit?.name || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Customer Information */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Customer Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Customer Name"
                    value={formData.customer_name}
                    onChange={handleChange('customer_name')}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Customer Email"
                    type="email"
                    value={formData.customer_email}
                    onChange={handleChange('customer_email')}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <PhoneInput
                    label="Customer Phone"
                    value={formData.customer_phone}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, customer_phone: val }))
                    }
                    required
                  />
                </Grid>
              </Grid>

              {/* Additional Customers Section (as per BookingForm) */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight={600}>Additional Customers</Typography>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Name"
                      value={formData.newCustomerName || ''}
                      onChange={e => setFormData(f => ({ ...f, newCustomerName: e.target.value }))}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Email"
                      value={formData.newCustomerEmail || ''}
                      onChange={e => setFormData(f => ({ ...f, newCustomerEmail: e.target.value }))}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <PhoneInput
                      label="Phone"
                      value={formData.newCustomerPhone}
                      onChange={(val) =>
                        setFormData((prev) => ({ ...prev, newCustomerPhone: val }))
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if ((formData.newCustomerName || '').trim()) {
                          setFormData(f => ({
                            ...f,
                            additional_customers: [
                              ...f.additional_customers,
                              {
                                name: f.newCustomerName,
                                email: f.newCustomerEmail,
                                phone: f.newCustomerPhone
                              }
                            ],
                            newCustomerName: '',
                            newCustomerEmail: '',
                            newCustomerPhone: ''
                          }));
                        }
                      }}
                      disabled={!(formData.newCustomerName || '').trim()}
                      fullWidth
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
                {/* Table of additional customers */}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.additional_customers && formData.additional_customers.length > 0 ? (
                        formData.additional_customers.map((customer, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{customer.name}</TableCell>
                            <TableCell>{customer.email}</TableCell>
                            <TableCell>{customer.phone}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => {
                                setFormData(f => ({
                                  ...f,
                                  newCustomerName: customer.name,
                                  newCustomerEmail: customer.email,
                                  newCustomerPhone: customer.phone,
                                  additional_customers: f.additional_customers.filter((_, i) => i !== idx)
                                }));
                              }}>
                                <EditIcon />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => {
                                setFormData(f => ({
                                  ...f,
                                  additional_customers: f.additional_customers.filter((_, i) => i !== idx)
                                }));
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No additional customers added.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>

            <Divider />

            {/* Finance Section (Grid-based, like BookingForm and as per image 1) */}
            <Box>
              <Typography variant="h6" gutterBottom>Finance</Typography>
              {/* Base Price */}
              <Box mb={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Base Price"
                      name="base_price"
                      value={formData.base_price}
                      onChange={handleChange('base_price')}
                      type="number"
                      fullWidth
                      required
                      inputProps={{ step: 1, min: 0 }}
                    />
                  </Grid>
                </Grid>
              </Box>
              {/* Booking Amount Section */}
              <Box mb={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Booking Amount %"
                      name="booking_amount_pct"
                      value={formData.booking_amount_pct || ''}
                      onChange={handleChange('booking_amount_pct')}
                      type="number"
                      fullWidth
                      inputProps={{ step: 1, min: 0, max: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Booking Amount"
                      name="booking_amount"
                      value={formData.booking_amount}
                      onChange={handleChange('booking_amount')}
                      type="number"
                      fullWidth
                      inputProps={{ step: 1, min: 0, max: parseFloat(formData.base_price) || 0 }}
                    />
                  </Grid>
                </Grid>
              </Box>
              {/* Token % and Token Amount */}
              <Box mb={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Token Amount %"
                      name="token_amount_pct"
                      value={formData.token_amount_pct || ''}
                      onChange={handleChange('token_amount_pct')}
                      type="number"
                      fullWidth
                      inputProps={{ step: 1, min: 0, max: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Token Amount"
                      name="token_amount"
                      value={formData.token_amount || ''}
                      onChange={handleChange('token_amount')}
                      type="number"
                      fullWidth
                      inputProps={{ step: 1, min: 0, max: parseFloat(formData.booking_amount) || 0 }}
                    />
                  </Grid>
                </Grid>
              </Box>
              
              {/* Other Charges Section (reuse existing renderOtherChargesSection) */}
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>Other Charges</Typography>
                <OtherChargesSection
                  charges={formData.other_charges}
                  onChange={(charges) => {
                    setFormData(f => ({ ...f, other_charges: charges }));
                    // Recalculate total price
                    const totalOtherCharges = charges.reduce((sum, charge) => sum + (parseInt(charge.amount) || 0), 0);
                    const tax = parseInt(formData.tax) || 0;
                    const subtotal = totalOtherCharges + formData.base_price;
                    const taxAmount = Math.floor(subtotal * tax / 100);
                    const total = subtotal + taxAmount;
                    setFormData(f => ({
                      ...f,
                      taxes: taxAmount,
                      subtotal: subtotal,
                      total_price: total
                    }));
                  }}
                  otherCharges={otherChargesSelect}
                />
              </Box>

              
              {/* Tax */}
              <Box mb={2}>
                <TextField
                  label="Tax %"
                  name="tax"
                  value={formData.tax || ''}
                  onChange={handleChange('tax')}
                  type="number"
                  fullWidth
                  inputProps={{ step: 1, min: 0 }}
                />
              </Box>
              {/* Tax Amount */}
              <Box mb={2}>
                <TextField
                  label="Tax Amount"
                  name="taxes"
                  value={formData.taxes}
                  type="number"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Box>
              {/* Total Amount */}
              <Box>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Base Price:</Typography>
                        <Typography variant="body2">₹{formData.base_price || '0'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Other Charges:</Typography>
                        <Typography variant="body2">
                          ₹{formData.other_charges.reduce((sum, charge) => sum + (charge.amount || 0), 0)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Subtotal:</Typography>
                        <Typography variant="body2">₹{formData.total_price || '0'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Tax:</Typography>
                        <Typography variant="body2">₹{formData.taxes || '0'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between" sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
                        <Typography variant="body1" fontWeight={600}>Total:</Typography>
                        <Typography variant="body1" fontWeight={600}>₹{formData.total_price || '0'}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Payment Plan */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Payment Plan
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Payment Plan</InputLabel>
                <Select
                  value={formData.payment_plan.type || ''}
                  onChange={handleChange('payment_plan')}
                  label="Payment Plan"
                >
                  {PAYMENT_PLANS.map((plan) => (
                    <MenuItem key={plan.value} value={plan.value}>
                      <Box>
                        <Typography variant="subtitle2">{plan.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {plan.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            {/* Dates */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Important Dates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Expiry Date"
                      value={formData.expiry_date}
                      onChange={handleDateChange('expiry_date')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Completion Date"
                      value={formData.completion_date}
                      onChange={handleDateChange('completion_date')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Box>

            {/* Additional Information */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={4}
                    value={formData.notes}
                    onChange={handleChange('notes')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.terms_accepted}
                        onChange={handleCheckboxChange('terms_accepted')}
                      />
                    }
                    label="Terms Accepted"
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={() => onClose()} 
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

